import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '../store'
import { getAuthStorageItem, getValidToken, redirectToLogin } from '../utils/authToken'
import { hasPermission, hasPermissions, isAdminUser, routePermission, visibleMenuItems } from '../utils/permissions'
import { getViewportWidth, shouldLockMobileShareToolsNavigation } from '../utils/mobileShareTools.mjs'

Vue.use(VueRouter)

const Login = () => import(/* webpackChunkName: "login" */ '../views/Login.vue')
const DashboardLayout = () => import(/* webpackChunkName: "layout" */ '../views/DashboardLayout.vue')
const DashboardHome = () => import(/* webpackChunkName: "dashboard" */ '../views/DashboardHome.vue')
const Warehouse = () => import(/* webpackChunkName: "warehouse" */ '../views/Warehouse.vue')
const Packing = () => import(/* webpackChunkName: "packing" */ '../views/Packing.vue')
const Customers = () => import(/* webpackChunkName: "customers" */ '../views/Customers.vue')
const WarehouseMobile = () => import(/* webpackChunkName: "warehouse-mobile" */ '../views/WarehouseMobile.vue')
const CustomerRegister = () => import(/* webpackChunkName: "customer-register" */ '../views/CustomerRegister.vue')
const CustomerTrack = () => import(/* webpackChunkName: "customer-track" */ '../views/CustomerTrack.vue')
const Settings = () => import(/* webpackChunkName: "settings" */ '../views/Settings.vue')
const MediaManagement = () => import(/* webpackChunkName: "media-management" */ '../views/MediaManagement.vue')
const Shipping = () => import(/* webpackChunkName: "shipping" */ '../views/Shipping.vue')
const PublicShippingCalculator = () => import(/* webpackChunkName: "shipping-calculator" */ '../views/PublicShippingCalculator.vue')
const Tracking = () => import(/* webpackChunkName: "tracking" */ '../views/Tracking.vue')
const FollowUpManagement = () => import(/* webpackChunkName: "follow-up" */ '../views/FollowUpManagement.vue')
const CustomerShare = () => import(/* webpackChunkName: "customer-share" */ '../views/CustomerShare.vue')
const CustomerShareGenerator = () => import(/* webpackChunkName: "customer-share-generator" */ '../views/CustomerShareGenerator.vue')
const Finance = () => import(/* webpackChunkName: "finance" */ '../views/Finance.vue')
const ChatManagement = () => import(/* webpackChunkName: "chat" */ '../views/ChatManagement.vue')
const Printing = () => import(/* webpackChunkName: "printing" */ '../views/Printing.vue')
const OutboundPending = () => import(/* webpackChunkName: "outbound-pending" */ '../views/OutboundPending.vue')
const OutboundManagement = () => import(/* webpackChunkName: "outbound" */ '../views/OutboundManagement.vue')

const USER_PROFILE_TTL = 60 * 1000
let userProfilePromise = null
let userProfileRefreshedAt = 0
let userProfileToken = ''

async function refreshUserProfile(token) {
  const now = Date.now()
  if (store.state.user && userProfileToken === token && now - userProfileRefreshedAt < USER_PROFILE_TTL) {
    return true
  }
  if (userProfilePromise && userProfileToken === token) {
    return userProfilePromise
  }
  userProfileToken = token
  userProfilePromise = fetch('/api/users/me', {
    headers: { Authorization: `Bearer ${token}` }
  }).then(async res => {
    if (res.ok) {
      const user = await res.json()
      store.commit('SET_USER', user)
      userProfileRefreshedAt = Date.now()
      return true
    }
    if (res.status === 401) {
      redirectToLogin()
      return false
    }
    if (store.state.user) {
      userProfileRefreshedAt = Date.now()
    }
    return true
  }).catch(() => {
    if (store.state.user) {
      userProfileRefreshedAt = Date.now()
    }
    return true
  }).finally(() => {
    userProfilePromise = null
  })
  return userProfilePromise
}

const routes = [
  { path: '/login', component: Login },
  {
    path: '/mobile',
    component: WarehouseMobile,
    meta: { requiresAuth: true }
  },
  { path: '/customer', component: CustomerRegister },
  { path: '/customer/:customerNo', component: CustomerTrack },
  {
    path: '/share/customer',
    component: CustomerShareGenerator,
    meta: { requiresAuth: true, permissions: ['customer_manage', 'customer_share_manage'] }
  },
  {
    path: '/share/customer/',
    component: CustomerShareGenerator,
    meta: { requiresAuth: true, permissions: ['customer_manage', 'customer_share_manage'] }
  },
  { path: '/share/customer/:customerNo', component: CustomerShare },
  { path: '/shipping-calculator', component: PublicShippingCalculator },
  {
    path: '/',
    component: DashboardLayout,
    meta: { requiresAuth: true },
    children: [
      { path: '', redirect: '/dashboard' },
      { path: 'dashboard', component: DashboardHome },
      { path: 'warehouse', component: Warehouse },
      { path: 'customers', component: Customers },
      { path: 'packing', component: Packing },
      { path: 'shipping', component: Shipping },
      { path: 'chat', component: ChatManagement },
      { path: 'tracking', component: Tracking },
      { path: 'follow-up', component: FollowUpManagement },
      { path: 'finance', component: Finance },
      { path: 'settings', component: Settings },
      { path: 'media-management', component: MediaManagement },
      { path: 'customer-share-tools', component: CustomerShareGenerator },
      { path: 'orders', redirect: '/printing' },
      { path: 'printing', component: Printing },
      { path: 'outbound-pending', component: OutboundPending },
      { path: 'outbound', component: OutboundManagement }
    ]
  }
]

const router = new VueRouter({
  mode: 'history',
  routes
})

router.beforeEach(async (to, from, next) => {
  if (shouldLockMobileShareToolsNavigation(from.path, to.path, getViewportWidth())) {
    next(false)
    return
  }

  const token = getValidToken()
  if (to.matched.some(record => record.meta.requiresAuth) && !token) {
    next({
      path: '/login',
      query: { redirect: to.fullPath }
    })
    return
  }
  if (to.matched.some(record => record.meta.requiresAuth) && token) {
    const ok = await refreshUserProfile(token)
    if (!ok) {
      return
    }
  }
  const metaPermissions = to.matched.map(record => record.meta && record.meta.permissions).find(Boolean)
  const metaPermission = to.matched.map(record => record.meta && record.meta.permission).find(Boolean)
  const required = metaPermissions
    ? { permissions: metaPermissions }
    : metaPermission
      ? { permission: metaPermission, aliases: [] }
    : routePermission(to.path)
  if (required) {
    const user = store.state.user || JSON.parse(getAuthStorageItem('user') || 'null') || {}
    const allowed = required.permissions && required.permissions.length
      ? hasPermissions(user, required.permissions)
      : hasPermission(user, required.permission, required.aliases)
    if ((required.adminOnly && !isAdminUser(user)) || !allowed) {
      const first = visibleMenuItems(user).find(item => item.path)
      next(first && first.path ? first.path : '/dashboard')
      return
    }
  }
  next()
})

export default router
